import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { resourceLevelsApi } from '@/utils/api';
import type { ResourceLevel, ResourceLevelCategory } from '@/types';
import type { RootState } from '../index';

interface ResourceLevelsState {
  entities: ResourceLevel[];
  currentEntity: ResourceLevel | null;
  loading: boolean;
  error: string | null;
}

const initialState: ResourceLevelsState = {
  entities: [],
  currentEntity: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchResourceLevels = createAsyncThunk(
  'resourceLevels/fetchAll',
  async (includeInactive: boolean | undefined, { rejectWithValue }) => {
    try {
      const response = await resourceLevelsApi.getAll(includeInactive);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch resource levels');
      }
      return (response.data as { resourceLevels: ResourceLevel[] })?.resourceLevels || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch resource levels');
    }
  }
);

export const fetchResourceLevelById = createAsyncThunk(
  'resourceLevels/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceLevelsApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch resource level');
      }
      return (response.data as { resourceLevel: ResourceLevel })?.resourceLevel || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch resource level');
    }
  }
);

export const createResourceLevel = createAsyncThunk(
  'resourceLevels/create',
  async (data: Partial<ResourceLevel>, { rejectWithValue }) => {
    try {
      const response = await resourceLevelsApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create resource level');
      }
      return (response.data as { resourceLevel: ResourceLevel })?.resourceLevel || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create resource level');
    }
  }
);

export const updateResourceLevel = createAsyncThunk(
  'resourceLevels/update',
  async ({ id, data }: { id: string; data: Partial<ResourceLevel> }, { rejectWithValue }) => {
    try {
      const response = await resourceLevelsApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update resource level');
      }
      return (response.data as { resourceLevel: ResourceLevel })?.resourceLevel || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update resource level');
    }
  }
);

export const deleteResourceLevel = createAsyncThunk(
  'resourceLevels/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceLevelsApi.delete(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete resource level');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete resource level');
    }
  }
);

const resourceLevelsSlice = createSlice({
  name: 'resourceLevels',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<ResourceLevel | null>) => {
      state.currentEntity = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchResourceLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceLevels.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchResourceLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchResourceLevelById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceLevelById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchResourceLevelById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createResourceLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createResourceLevel.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.push(action.payload);
          // Sort by sortOrder after adding
          state.entities.sort((a, b) => a.sortOrder - b.sortOrder);
        }
      })
      .addCase(createResourceLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateResourceLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateResourceLevel.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(rl => rl.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
            // Re-sort if sortOrder changed
            state.entities.sort((a, b) => a.sortOrder - b.sortOrder);
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateResourceLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteResourceLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteResourceLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(rl => rl.id !== action.payload);
        if (state.currentEntity?.id === action.payload) {
          state.currentEntity = null;
        }
      })
      .addCase(deleteResourceLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentEntity } = resourceLevelsSlice.actions;

// Selectors
export const selectAllResourceLevels = (state: RootState) => state.resourceLevels.entities;
export const selectActiveResourceLevels = (state: RootState) =>
  state.resourceLevels.entities.filter(rl => rl.isActive);
export const selectResourceLevelsByCategory = (state: RootState, category: ResourceLevelCategory) =>
  state.resourceLevels.entities.filter(rl => rl.category === category && rl.isActive);
export const selectResourceLevelsLoading = (state: RootState) => state.resourceLevels.loading;
export const selectResourceLevelsError = (state: RootState) => state.resourceLevels.error;
export const selectCurrentResourceLevel = (state: RootState) => state.resourceLevels.currentEntity;

export default resourceLevelsSlice.reducer;
